/**
 * Is the soft keyboard currently on screen?
 *
 * Deliberately built on React Native's own `Keyboard` events rather than
 * `react-native-keyboard-controller`'s `useKeyboardState`: this hook is called
 * from `AppModal`, which renders on web too, and `KeyboardProvider` is mounted
 * on native only (`src/app/_layout.tsx`). RN's listeners simply never fire on
 * web, which is the correct answer there — a web page has no soft keyboard.
 *
 * WHY THIS EXISTS
 * ---------------
 * `AppModal`'s backdrop press called `Keyboard.dismiss()` AND `onClose()`
 * together, so the only gesture that hid the keyboard also destroyed the modal
 * and whatever had been typed into it. There was no way to dismiss the keyboard
 * and keep looking at the form — one half of the long-standing "modals are
 * unusable on mobile" complaint.
 *
 * Knowing whether the keyboard is up lets the backdrop do the obvious thing:
 * first tap puts the keyboard away, second tap closes the modal.
 */

import { useEffect, useState } from 'react';
import { Keyboard, Platform } from 'react-native';

export function useKeyboardVisible(): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    // `Will*` on iOS fires with the animation, so the UI reacts in step with
    // the keyboard rather than a frame after it. Android only emits `Did*`.
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, () => setVisible(true));
    const hideSub = Keyboard.addListener(hideEvent, () => setVisible(false));

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  return visible;
}
